import { useRouter } from 'next/router';
import { useState } from 'react';

import { useToastErrorHandler } from '@collab/libs/errors';

import {
  FeedbackBody,
  FeedbackType,
  sendFeedback as sendFeedbackCall,
} from './feedback.api';

type Feedback = {
  rating: number;
  email: string;
  message: string;
  path: string;
};

const useSendFeedback = (onCompleted: () => void) => {
  const { asPath } = useRouter();
  const handleWithToast = useToastErrorHandler();
  const [isLoading, setIsLoading] = useState(false);

  const sendFeedback = async (feedback: Omit<Feedback, 'path'>) => {
    setIsLoading(true);

    const formData: Feedback = { ...feedback, path: asPath };

    try {
      const body = formDataToFeedbackRequestBody(formData);

      await sendFeedbackCall(body);

      onCompleted();
    } catch (e) {
      handleWithToast(e, feedbackErrorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return { sendFeedback, isLoading };
};

const formDataToFeedbackRequestBody = (data: Feedback): FeedbackBody => {
  const type =
    data.rating === 1 ? FeedbackType.POSITIVE : FeedbackType.NEGATIVE;

  return {
    message: data.message,
    email: data.email,
    path: data.path,
    type,
  };
};

const feedbackErrorMsg = {
  text: 'We could not send the feedback. Please try again.',
  key: 'send-feedback',
};

export default useSendFeedback;
